import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { experienceService } from '../services/experience.service';
import type { UpdateExperienceStatusRequest, Experience } from '../types/experience.types';

interface UpdateExperienceStatusData {
  candidateId: string;
  experienceId: string;
  request: UpdateExperienceStatusRequest;
}

type StatusMutation = UseMutationResult<Experience, Error, UpdateExperienceStatusData>;

interface UseUpdateExperienceStatusReturn {
  updateExperienceStatus: StatusMutation['mutate'];
  updateExperienceStatusAsync: StatusMutation['mutateAsync'];
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
  reset: () => void;
}

export function useUpdateExperienceStatus(): UseUpdateExperienceStatusReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: UpdateExperienceStatusData) =>
      experienceService.updateExperienceStatus(data.candidateId, data.experienceId, data.request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['experiences', { candidateId: variables.candidateId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['experience', variables.candidateId, variables.experienceId],
      });
    },
  });

  return {
    updateExperienceStatus: mutation.mutate,
    updateExperienceStatusAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    reset: mutation.reset,
  };
}
